import APIs from '../../../api'
import { apiClient } from '../../../api/client'

const normalizeInputModule = (module = {}) => {
  const sensor =
    module.sensor || (module.sensorName ? { name: module.sensorName, unit: null } : null)
  const attachedDevice = module.attachedDevice || module.attachWithDevice || null

  return {
    ...module,
    isOn: module.isOn ?? false,
    sensor,
    attachedDevice,
    vTag: module.vTag ?? module.vTagId ?? '',
    facilityArea: module.facilityArea ?? module.facility?.name ?? '',
  }
}

// GET /input-modules — list input modules with server-side pagination,
// search, sorting, and the same positional `filterFields` / `filterInputs`
// filter convention used by `listDevicesApi` and `listExternalModulesApi`.
// The response body is normalized to the UI shape the table renders so the
// page stays compatible with the existing view.
const listInputModulesApi = ({
  per_page = 10,
  current_page = 1,
  search,
  orderBy,
  order,
  filterFields,
  filterInputs,
} = {}) => {
  const query = { page: current_page, limit: per_page }

  if (search) query.search = search

  // Mirrors the external-modules / devices filters: walk the parallel
  // `filterFields` / `filterInputs` arrays the table passes in and
  // append each non-empty value to the query. Empty / null values are
  // skipped so we never send `sensorId=` (which the backend treats as
  // "filter to no records" rather than "no constraint").
  filterFields?.forEach((field, index) => {
    const value = filterInputs?.[index]
    if (value !== '' && value != null) query[field] = value
  })

  if (orderBy) query.orderBy = orderBy
  if (order) query.order = order

  return apiClient.get(APIs.INPUT_MODULES.list, { params: query }).then((response) => {
    const payload = response?.data || {}
    const normalizedData = Array.isArray(payload.data) ? payload.data.map(normalizeInputModule) : []

    return {
      ...response,
      data: {
        ...payload,
        data: normalizedData,
      },
    }
  })
}

export { listInputModulesApi }

// POST /input-modules — register a new input module. Body shape mirrors
// `createExternalModuleApi`: attach-by-device id, optional V-Tag id,
// optional attached-sensor id, calibration expiry, and the
// auto-create-asset-tag flags. The request is sent as `multipart/form-data`
// because the payload can include a calibration certificate file when the
// user attached a sensor — backend uploads it to S3 and stores the
// resulting calibration record id. Empty / null values are dropped so we
// never append `sensorId: ""` or `calibrationExpiryDate: ""`.
const createInputModuleApi = (payload = {}, calibrationFile = null) => {
  const body = new FormData()

  Object.entries(payload).forEach(([key, value]) => {
    if (value == null || value === '') return
    body.append(key, Array.isArray(value) ? value.join(',') : String(value))
  })

  if (calibrationFile) body.append('calibrationCertificate', calibrationFile)

  return apiClient.post(APIs.INPUT_MODULES.create, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export { createInputModuleApi }

// GET /input-modules/input-types — fetch the dropdown options for the
// "Input type" picker. The endpoint returns a plain JSON envelope of the
// shape `{ success, message, data: [{ id, uuid, name, slug }, …] }`. We
// unwrap `data` so the caller can treat the response as the options array
// directly, mirroring how the other paginated lists expose their payload.
const listInputTypesApi = () =>
  apiClient.get(APIs.INPUT_MODULES.inputTypes).then((response) => response?.data?.data ?? [])

export { listInputTypesApi }

// GET /input-modules/{uuid} — fetch the full input-module record for the
// Duplicate flow. The list payload is paginated and doesn't carry every
// field the registration modal needs (sensor/brand/attachedDevice as
// full objects), so we hit the by-uuid endpoint to seed the form.
const getInputModuleByUuidApi = (uuid) => apiClient.get(APIs.INPUT_MODULES.byUuid(uuid))

// GET /input-modules/unattached — input modules not currently coupled to
// any V-Tag. Backed by the same backend DTO contract as
// `listInputModulesApi` (page/limit + optional filters) so the couple/
// swap modal can list free input modules when the V-Tag's `type` is
// INPUT_MODULE, mirroring the /devices/unattached experience for the
// DEVICE branch. `orderBy` and `order` are forwarded verbatim so
// sortable server-side ordering (`createdAt`, …) works the same as the
// input-module list. The fetch layers in the V-Tags tab wrap this in
// `fetchInputModulesForDropdown` which returns `response.data` so the
// CustomAutoSelect's `Array.isArray(res?.data)` check still passes.
const listUnattachedInputModules = (params = {}) => {
  const { per_page, current_page, page, limit, search, orderBy, order } = params || {}
  const query = { page: current_page ?? page, limit: per_page ?? limit }
  if (search) query.search = search
  if (orderBy) query.orderBy = orderBy
  if (order) query.order = order
  return apiClient.get(APIs.INPUT_MODULES.unattached, { params: query })
}

export { getInputModuleByUuidApi, listUnattachedInputModules }

// PUT /input-modules/{uuid} — input-module update. Mirrors the create
// endpoint's multipart body shape so the backend accepts the same
// field set on the same route. Triggered from the row-level Edit action;
// `calibrationFile` is the optional new calibration certificate — the
// existing file is preserved server-side when no file is appended.
const updateInputModuleApi = (uuid, payload = {}, calibrationFile = null) => {
  const body = new FormData()

  Object.entries(payload).forEach(([key, value]) => {
    if (value == null || value === '') return
    body.append(key, Array.isArray(value) ? value.join(',') : String(value))
  })

  if (calibrationFile) body.append('calibrationCertificate', calibrationFile)

  return apiClient.put(APIs.INPUT_MODULES.update(uuid), body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export { updateInputModuleApi }

// GET /input-modules/export — streams a CSV of the filtered input-module
// set. The backend honours the same search/filter/sort params as
// `listInputModulesApi` (moduleName, sensorId, manufacturerId, …) but
// ignores `page`/`limit` so the export contains every matching row.
// `columns` is an array; axios serializes it as repeated
// `columns=…&columns=…` query params which matches the documented curl.
// `responseType: 'blob'` keeps the CSV payload opaque so axios doesn't
// try to JSON-parse a `text/csv` body. Mirrors `exportExternalModulesCsv`
// / `exportAccessPointsCsv` / `exportDevicesCsv` so the Sensors page can
// wire every tab through the same `Export CSV` action menu.
const exportInputModulesCsv = async ({ columns, orderBy, order, ...filters } = {}) => {
  const params = {}
  if (columns?.length) params.columns = columns
  if (orderBy) params.orderBy = orderBy
  if (order) params.order = order
  // Forward every remaining filter key as-is. Empty strings are dropped
  // server-side, so sending `''` for unset filters is harmless — but we
  // strip them here to keep the URL clean.
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== '' && v != null) params[k] = v
  })
  const response = await apiClient.get(APIs.INPUT_MODULES.export, {
    params,
    responseType: 'blob',
  })
  return response.data
}

export { exportInputModulesCsv }

// Export `APIs` for symmetry with the other module API files.
export { APIs }
